import moment from "moment"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import _get from "lodash/get"
import styled from "styled-components"
import { Link } from "react-router-dom"

import { checkPermission } from "../../../../helper/functions"
import User from "../../../../libs/admin"
import { getMemberVoteList, getNewsVotePackages, getNewsVotes } from "./service"
import PaginationNew from "./components/PaginationNew"
import DropdownFilter from "./components/DropdownFilter"
import {
	filterInputStyle,
	primaryButtonStyle,
	secondaryButtonStyle,
	tableStyle,
	thStyle,
	tdStyle,
	mainContainerStyle,
	headerSectionStyle,
	headerTitleStyle,
} from "./styles/commonStyles"

const getDefaultFilters = () => ({
	keyword: "",
	campaignIds: [],
	packageIds: [],
	paymentTypes: [],
	orderId: "",
	messageId: "",
	startDate: null,
	endDate: null,
})

const paymentTypeOptions = [
	{ id: "payment", name: "Payment" },
	{ id: "sms", name: "SMS" },
]

const createMemberListCondition = (filters) => {
	const condition = {}
	const keyword = typeof filters.keyword === "string" ? filters.keyword.trim() : ""
	if (keyword) condition.keyword = keyword

	const orderId = typeof filters.orderId === "string" ? filters.orderId.trim() : ""
	if (orderId) condition.orderId = orderId

	const messageId = typeof filters.messageId === "string" ? filters.messageId.trim() : ""
	if (messageId) condition.messageId = messageId

	const cleanedCampaignIds = Array.isArray(filters.campaignIds) ? filters.campaignIds.filter(Boolean) : []
	if (cleanedCampaignIds.length > 0) condition.campaignIds = cleanedCampaignIds

	const cleanedPackageIds = Array.isArray(filters.packageIds) ? filters.packageIds.filter(Boolean) : []
	if (cleanedPackageIds.length > 0) condition.packageIds = cleanedPackageIds

	const cleanedPaymentTypes = Array.isArray(filters.paymentTypes) ? filters.paymentTypes.filter(Boolean) : []
	if (cleanedPaymentTypes.length > 0) condition.paymentTypes = cleanedPaymentTypes

	if (filters.startDate) condition.startDate = moment(filters.startDate).startOf("day").toISOString()
	if (filters.endDate) condition.endDate = moment(filters.endDate).endOf("day").toISOString()

	return condition
}

const FilterWrapper = styled.div`
	background: #f9f9f9;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	padding: 20px;
	margin-bottom: 24px;
	box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
`

const FilterGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(1, minmax(0, 1fr));
	gap: 1.25rem;

	.form-input {
		margin-bottom: 0;
	}

	.filter-full {
		grid-column: 1 / -1;
	}

	.filter-row {
		display: grid;
		grid-template-columns: 1.5fr 1.5fr 1.2fr 1.1fr 1.1fr auto;
		gap: 1rem;
		align-items: flex-end;
		width: 100%;
	}
	.filter-row > * {
		width: 100%;
	}

	.filter-actions {
		display: flex;
		justify-content: flex-end;
		width: 100%;
		gap: 0.75rem;
		flex-wrap: wrap;
		align-items: center;
		align-self: flex-end;
	}

	@media (max-width: 992px) {
		.filter-row {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.filter-actions {
			justify-content: flex-start;
		}
	}

	@media (max-width: 600px) {
		.filter-row {
			grid-template-columns: repeat(1, minmax(0, 1fr));
		}
		.filter-actions {
			justify-content: flex-start;
		}
		.filter-row > * {
			width: 100%;
		}
	}
`

const filterLabelStyle = {
	display: "block",
	fontSize: "0.75rem",
	color: "#6b7280",
	marginBottom: 4,
	fontWeight: 500,
}

const searchButtonStyle = {
	...primaryButtonStyle,
	padding: "0 20px",
	height: "40px",
	minWidth: "96px",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
}

const resetButtonStyle = {
	...secondaryButtonStyle,
	padding: "0 20px",
	height: "40px",
	minWidth: "96px",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
}

const errorMessageStyle = {
	background: "#fee2e2",
	border: "1px solid #fecaca",
	color: "#b91c1c",
	padding: "12px 16px",
	borderRadius: "8px",
	marginBottom: "16px",
}

const detailLinkStyle = {
	color: "#2563eb",
	textDecoration: "none",
	fontWeight: 500,
}

const MemberVoteList = (props) => {
	const user = User.getCookieData()
	const [filters, setFilters] = useState(() => getDefaultFilters())
	const [appliedFilters, setAppliedFilters] = useState(() => getDefaultFilters())
	const [campaignList, setCampaignList] = useState([])
	const [packageList, setPackageList] = useState([])
	const [isCampaignDropdownOpen, setIsCampaignDropdownOpen] = useState(false)
	const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false)
	const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false)
	const [members, setMembers] = useState([])
	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(10)
	const [total, setTotal] = useState(0)
	const [isLoading, setIsLoading] = useState(true)
	const [errorMessage, setErrorMessage] = useState("")
	const hasPermission = useMemo(() => checkPermission(user.permission, "news_vote_view"), [user.permission])
	const campaignDropdownRef = useRef(null)
	const packageDropdownRef = useRef(null)
	const paymentDropdownRef = useRef(null)

	const fetchCampaignList = useCallback(async () => {
		try {
			const response = await getNewsVotes({
				condition: {},
				page: 1,
				limit: 200,
				sort: { _id: -1 },
			})
			if (response.status === 200 && response.data?.status === 200) {
				const campaigns = response.data.data?.campaigns || []
				const options = campaigns
					.map((item) => ({
						id: item._id,
						name: _get(item, "setting.name") || item.name || "ไม่ระบุชื่อ",
					}))
					.filter((item) => !!item.id)
				setCampaignList(options)
			} else {
				setCampaignList([])
			}
		} catch (error) {
			console.error("fetchCampaignList error", error)
			setCampaignList([])
		}
	}, [])

	const fetchCampaignOptions = useCallback(async () => {
		try {
			const response = await getNewsVotePackages()
			if (response.status === 200 && response.data?.status === 200) {
				const list = response.data.data || []
				const options = list
					.map((item) => {
						const id = item._id || item.id
						if (!id) return null
						const baseName = item.title || item.name || _get(item, "setting.name") || "ไม่ระบุชื่อ"
						const shortCode = item.shortCode || item.code
						const name = shortCode ? `${baseName} (${shortCode})` : baseName
						return { id, name }
					})
					.filter(Boolean)
				setPackageList(options)
			} else {
				setPackageList([])
			}
		} catch (error) {
			console.error("fetchCampaignOptions error", error)
			setPackageList([])
		}
	}, [])

	useEffect(() => {
		if (!hasPermission) {
			props.history.push("/")
		}
	}, [hasPermission, props.history])

	useEffect(() => {
		fetchCampaignList()
		fetchCampaignOptions()
	}, [fetchCampaignList, fetchCampaignOptions])

	const condition = useMemo(() => createMemberListCondition(appliedFilters), [appliedFilters])

	useEffect(() => {
		if (!hasPermission) return

		let isCancelled = false

		const fetchData = async () => {
			setIsLoading(true)
			setErrorMessage("")

			try {
				const response = await getMemberVoteList({
					page,
					limit,
					condition: { ...condition },
				})

				if (isCancelled) return

				const { status, data } = response
				if (status === 200 && data?.status === 200) {
					const payload = data.data || {}
					const items = payload.data || payload.memberVotes || payload.members || payload.items || []
					setMembers(items)
					setTotal(payload.total || 0)
					if (payload.page && payload.page !== page) setPage(payload.page)
					if (payload.limit && payload.limit !== limit) setLimit(payload.limit)
				} else {
					setErrorMessage(data?.message || "ไม่สามารถดึงข้อมูลได้ในขณะนี้")
					setMembers([])
					setTotal(0)
				}
			} catch (error) {
				if (isCancelled) return
				console.error("loadMemberVotes error", error)
				setErrorMessage("ไม่สามารถดึงข้อมูลได้ในขณะนี้")
				setMembers([])
				setTotal(0)
			} finally {
				if (isCancelled) return
				setIsLoading(false)
			}
		}

		fetchData()

		return () => {
			isCancelled = true
		}
	}, [condition, hasPermission, limit, page])

	const handleFilterChange = ({ name, value }) => {
		let nextValue = value
		if (name === "keyword") {
			nextValue = typeof nextValue === "string" ? nextValue.trimStart() : nextValue
		}
		setFilters((prev) => ({
			...prev,
			[name]: nextValue,
		}))
	}

	const handleSearch = (event) => {
		event.preventDefault()
		setPage(1)
		setAppliedFilters({
			...filters,
			campaignIds: [...filters.campaignIds],
			packageIds: [...filters.packageIds],
			paymentTypes: [...filters.paymentTypes],
		})
		setIsCampaignDropdownOpen(false)
		setIsPackageDropdownOpen(false)
		setIsPaymentDropdownOpen(false)
	}

	const handleReset = () => {
		const defaultFilters = getDefaultFilters()
		setFilters(defaultFilters)
		setAppliedFilters(getDefaultFilters())
		setPage(1)
		setLimit(20)
		setIsCampaignDropdownOpen(false)
		setIsPackageDropdownOpen(false)
		setIsPaymentDropdownOpen(false)
	}

	const handleChangePage = (nextPage) => {
		setPage(nextPage)
	}

	const handleChangeLimit = (nextLimit) => {
		const parsedLimit = Number(nextLimit) || limit
		setLimit(parsedLimit)
		const resetPage = 1
		setPage(resetPage)
	}

	const handleDateInputChange = (name) => (event) => {
		const { value } = event.target
		const nextValue = value ? moment(value, "YYYY-MM-DD").toISOString() : null
		handleFilterChange({ name, value: nextValue })
	}

	const handleToggleCampaign = (campaignId) => {
		setFilters((prev) => {
			const exists = prev.campaignIds.includes(campaignId)
			const nextIds = exists ? prev.campaignIds.filter((id) => id !== campaignId) : [...prev.campaignIds, campaignId]
			return { ...prev, campaignIds: nextIds }
		})
	}

	const handleSelectAllCampaigns = () => {
		setFilters((prev) => {
			if (campaignList.length === 0) return { ...prev, campaignIds: [] }
			const allIds = campaignList.map((c) => c.id)
			const allSelected = prev.campaignIds.length === allIds.length
			return { ...prev, campaignIds: allSelected ? [] : allIds }
		})
	}

	const handleTogglePackage = (packageId) => {
		setFilters((prev) => {
			const exists = prev.packageIds.includes(packageId)
			const nextIds = exists ? prev.packageIds.filter((id) => id !== packageId) : [...prev.packageIds, packageId]
			return { ...prev, packageIds: nextIds }
		})
	}

	const handleSelectAllPackages = () => {
		setFilters((prev) => {
			if (packageList.length === 0) return { ...prev, packageIds: [] }
			const allIds = packageList.map((c) => c.id)
			const allSelected = prev.packageIds.length === allIds.length
			return { ...prev, packageIds: allSelected ? [] : allIds }
		})
	}

	const handleTogglePaymentType = (paymentType) => {
		setFilters((prev) => {
			const exists = prev.paymentTypes.includes(paymentType)
			const nextTypes = exists ? prev.paymentTypes.filter((item) => item !== paymentType) : [...prev.paymentTypes, paymentType]
			return { ...prev, paymentTypes: nextTypes }
		})
	}

	const handleSelectAllPaymentTypes = () => {
		setFilters((prev) => {
			if (paymentTypeOptions.length === 0) return { ...prev, paymentTypes: [] }
			const allTypes = paymentTypeOptions.map((opt) => opt.id)
			const allSelected = prev.paymentTypes.length === allTypes.length
			return { ...prev, paymentTypes: allSelected ? [] : allTypes }
		})
	}

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(event.target)) {
				setIsCampaignDropdownOpen(false)
			}
			if (packageDropdownRef.current && !packageDropdownRef.current.contains(event.target)) {
				setIsPackageDropdownOpen(false)
			}
			if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
				setIsPaymentDropdownOpen(false)
			}
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const memberList = Array.isArray(members) ? members : []
	const totalPages = Math.max(1, Math.ceil(total / limit) || 1)

	const getCampaignName = (row) => row?.campaignName || _get(row, "campaignDetail.campaignName") || _get(row, "newsVote.setting.name") || "-"
	const getPlayerName = (row) => _get(row, "memberInfo.name")
	const getMemberEmail = (row) => _get(row, "memberInfo.email")
	const getMemberPhone = (row) => (row.paymentType === "payment" ? _get(row, "memberInfo.msisdn") : _get(row, "smsDetail.msisdn"))
	const getContact = (row) => {
		const email = getMemberEmail(row)
		const phone = getMemberPhone(row)

		return phone || email || "-"
	}
	const getMemberIdentifier = (row) => row?.userId
	const renderContactCell = (row) => {
		const email = getMemberEmail(row)
		const phone = getMemberPhone(row)
		const identifier = getMemberIdentifier(row)

		if (!email && !phone) {
			return "-"
		}

		const params = new URLSearchParams()
		params.append("type", row.paymentType)
		const search = params.toString()
		const to = {
			pathname: `/campaign-new/member-vote/detail/${encodeURIComponent(String(identifier))}`,
			search: search ? `?${search}` : "",
			state: { member: row },
		}

		return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{phone || email || <></>}</div>

		// return (
		// 	<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
		// 		{email ? (
		// 			<Link to={to} style={detailLinkStyle}>
		// 				{email}
		// 			</Link>
		// 		) : null}
		// 		{phone ? (
		// 			<Link to={to} style={detailLinkStyle}>
		// 				{phone}
		// 			</Link>
		// 		) : null}
		// 	</div>
		// )
	}
	const getMembershipLabel = (row) => {
		const value = row?.isMembership
		const userMembership = _get(row, "user.isMembership")
		const membershipType = _get(row, "membershipType")
		const resolved =
			typeof value === "boolean"
				? value
				: typeof userMembership === "boolean"
				? userMembership
				: membershipType
				? membershipType === "member"
				: undefined
		if (resolved === true) return "สมาชิก"
		if (resolved === false) return "ทั่วไป"
		return "-"
	}
	const getVoteTitle = (row) => _get(row, "campaignDetail.questionTitle") || _get(row, "vote.title") || "-"
	const getChoiceTitle = (row) => row?.choiceName || _get(row, "campaignDetail.choiceTitle") || _get(row, "choice.title") || "-"
	const getPaymentTypeLabel = (row) => {
		const type = row?.paymentType || _get(row, "paymentType")
		if (type === "payment") return "Payment"
		if (type === "sms") return "SMS"
		return "-"
	}
	const getPackageLabel = (row) => {
		const pkgTitle = _get(row, "package.title")
		const shortCode = _get(row, "package.shortCode")
		if (pkgTitle && shortCode) return `${pkgTitle}`
		return pkgTitle || "-"
	}
	const getTransactionId = (row) => {
		const type = row?.paymentType || _get(row, "paymentType")
		if (type === "payment") {
			const orderId = _get(row, "paymentDetail.orderId")
			const paymentMethod = _get(row, "paymentDetail.paymentMethod")
			let result = ""
			if (orderId) result += orderId
			if (paymentMethod) result += result ? ` (${paymentMethod})` : paymentMethod
			return result || "-"
		}
		if (type === "sms") return _get(row, "smsDetail.message_id") || "-"
		return "-"
	}
	const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-")

	const tableWrapperStyle = {
		overflowX: "auto",
		overflowY: "hidden",
		width: "100%",
		position: "relative",
	}

	const stickyColumnOrder = [
		{ key: "index", width: 50 },
		{ key: "voteDate", width: 150 },
		{ key: "campaignName", width: 220 },
		{ key: "voteTitle", width: 180 },
		// { key: "choiceTitle", width: 220 },
	]

	const stickyColumnsConfig = stickyColumnOrder.reduce((acc, column, index) => {
		const left = stickyColumnOrder.slice(0, index).reduce((sum, item) => sum + item.width, 0)
		acc[column.key] = {
			left,
			width: column.width,
		}
		return acc
	}, {})

	const getHeaderCellStyle = (column) => {
		const style = { ...thStyle }
		if (column.width) {
			style.width = column.width
			style.minWidth = column.width
		}
		if (column.minWidth) {
			style.minWidth = column.minWidth
		}
		if (column.align) {
			style.textAlign = column.align
		}
		style.whiteSpace = column.allowWrap ? "normal" : "nowrap"

		const stickyConfig = stickyColumnsConfig[column.key]
		if (stickyConfig) {
			style.position = "sticky"
			style.left = `${stickyConfig.left}px`
			style.zIndex = 3
			style.background = "#f9fafb"
			style.boxShadow = "inset -1px 0 0 #e5e7eb"
		}

		return style
	}

	const getBodyCellStyle = (column) => {
		const style = { ...tdStyle }
		if (column.width) {
			style.width = column.width
			style.minWidth = column.width
		}
		if (column.minWidth) {
			style.minWidth = column.minWidth
		}
		if (column.align) {
			style.textAlign = column.align
		}
		const shouldWrap = column.allowWrap === true
		style.whiteSpace = shouldWrap ? "normal" : "nowrap"
		style.wordBreak = shouldWrap ? "break-word" : "normal"
		style.overflow = shouldWrap ? "visible" : "hidden"
		style.textOverflow = shouldWrap ? "clip" : "ellipsis"

		const stickyConfig = stickyColumnsConfig[column.key]
		if (stickyConfig) {
			style.position = "sticky"
			style.left = `${stickyConfig.left}px`
			style.zIndex = 2
			style.background = "#ffffff"
			style.boxShadow = "inset -1px 0 0 #e5e7eb"
		}

		return style
	}

	const columns = [
		{
			key: "index",
			label: "#",
			width: stickyColumnsConfig.index?.width ?? 60,
			align: "center",
			render: (_, { globalIndex }) => globalIndex,
		},
		{
			key: "voteDate",
			label: "วันที่โหวต",
			width: stickyColumnsConfig.voteDate?.width ?? 180,
			render: (item) => formatDateTime(item?.createdAt),
		},
		{
			key: "campaignName",
			label: "แคมเปญ",
			width: stickyColumnsConfig.campaignName?.width ?? 200,
			allowWrap: true,
			render: (item) => getCampaignName(item),
		},
		{
			key: "voteTitle",
			label: "คำถาม",
			width: stickyColumnsConfig.voteTitle?.width ?? 240,
			allowWrap: true,
			render: (item) => getVoteTitle(item),
		},
		{
			key: "choiceTitle",
			label: "คำตอบ",
			width: stickyColumnsConfig.choiceTitle?.width ?? 200,
			allowWrap: true,
			render: (item) => getChoiceTitle(item),
		},
		{
			key: "playerName",
			label: "ชื่อ/นามสกุล",
			minWidth: 160,
			render: (item) => getPlayerName(item),
		},
		{
			key: "contact",
			label: "อีเมล / เบอร์โทรศัพท์",
			minWidth: 220,
			allowWrap: true,
			render: (item) => renderContactCell(item),
		},
		{
			key: "paymentType",
			label: "ช่องทาง",
			width: 120,
			align: "center",
			render: (item) => getPaymentTypeLabel(item),
		},
		{
			key: "package",
			label: "แพ็กเกจ",
			minWidth: 160,
			allowWrap: true,
			render: (item) => getPackageLabel(item),
		},
		{
			key: "transaction",
			label: "Order ID / Message ID",
			minWidth: 220,
			allowWrap: true,
			render: (item) => getTransactionId(item),
		},
	]

	return (
		<div style={mainContainerStyle}>
			<div style={headerSectionStyle}>
				<h2 style={headerTitleStyle}>รายการผู้เล่น News Vote (เสียเงิน)</h2>
			</div>

			<form onSubmit={handleSearch}>
				<FilterWrapper>
					<FilterGrid>
						<div className="filter-full">
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "2fr 1fr 1fr",
									gap: "1rem",
									alignItems: "flex-end",
								}}
							>
								<div>
									<label style={filterLabelStyle}>ค้นหา</label>
									<input
										type="text"
										name="keyword"
										placeholder="ชื่อ, อีเมล, เบอร์โทรศัพท์"
										value={filters.keyword}
										onChange={(e) => handleFilterChange({ name: "keyword", value: e.target.value })}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												handleSearch(e)
											}
										}}
										style={{ ...filterInputStyle, width: "100%" }}
									/>
								</div>
								<div>
									<label style={filterLabelStyle}>วันที่เริ่ม</label>
									<input
										type="date"
										value={filters.startDate ? moment(filters.startDate).format("YYYY-MM-DD") : ""}
										onChange={handleDateInputChange("startDate")}
										style={{ ...filterInputStyle, width: "100%" }}
									/>
								</div>
								<div>
									<label style={filterLabelStyle}>วันที่สิ้นสุด</label>
									<input
										type="date"
										value={filters.endDate ? moment(filters.endDate).format("YYYY-MM-DD") : ""}
										onChange={handleDateInputChange("endDate")}
										style={{ ...filterInputStyle, width: "100%" }}
									/>
								</div>
							</div>
						</div>
						<div className="filter-row">
							<div>
								<label style={filterLabelStyle}>แคมเปญ</label>
								<DropdownFilter
									selectedIds={filters.campaignIds}
									data={campaignList}
									isOpen={isCampaignDropdownOpen}
									setIsOpen={setIsCampaignDropdownOpen}
									dropdownRef={campaignDropdownRef}
									onToggle={handleToggleCampaign}
									onSelectAll={handleSelectAllCampaigns}
									placeholder="แคมเปญทั้งหมด"
								/>
							</div>
							<div>
								<label style={filterLabelStyle}>แพ็กเกจ</label>
								<DropdownFilter
									selectedIds={filters.packageIds}
									data={packageList}
									isOpen={isPackageDropdownOpen}
									setIsOpen={setIsPackageDropdownOpen}
									dropdownRef={packageDropdownRef}
									onToggle={handleTogglePackage}
									onSelectAll={handleSelectAllPackages}
									placeholder="แพ็กเกจทั้งหมด"
								/>
							</div>
							<div>
								<label style={filterLabelStyle}>รหัสคำสั่งซื้อ(Web)</label>
								<input
									type="text"
									value={filters.orderId}
									onChange={(e) => handleFilterChange({ name: "orderId", value: e.target.value })}
									placeholder=""
									style={{ ...filterInputStyle, width: "100%" }}
								/>
							</div>
							<div>
								<label style={filterLabelStyle}>Message ID(SMS)</label>
								<input
									type="text"
									value={filters.messageId}
									onChange={(e) => handleFilterChange({ name: "messageId", value: e.target.value })}
									placeholder=""
									style={{ ...filterInputStyle, width: "100%" }}
								/>
							</div>
							<div>
								<label style={filterLabelStyle}>ช่องทาง</label>
								<DropdownFilter
									selectedIds={filters.paymentTypes}
									data={paymentTypeOptions}
									isOpen={isPaymentDropdownOpen}
									setIsOpen={setIsPaymentDropdownOpen}
									dropdownRef={paymentDropdownRef}
									onToggle={handleTogglePaymentType}
									onSelectAll={handleSelectAllPaymentTypes}
									placeholder="ช่องทางทั้งหมด"
								/>
							</div>
							<div className="filter-actions">
								<button type="button" style={resetButtonStyle} onClick={handleReset}>
									ล้างค่า
								</button>
								<button type="submit" style={searchButtonStyle}>
									ค้นหา
								</button>
							</div>
						</div>
					</FilterGrid>
				</FilterWrapper>
			</form>

			{/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, color: "#4b5563", fontSize: "0.95rem" }}>
				<span>
					พบผลลัพธ์ทั้งหมด <strong>{total.toLocaleString("th-TH")}</strong> รายการ
				</span>
			</div> */}

			{errorMessage && <div style={errorMessageStyle}>{errorMessage}</div>}

			<div style={tableWrapperStyle}>
				<table style={{ ...tableStyle, overflow: "visible" }}>
					<thead>
						<tr>
							{columns.map((column) => (
								<th key={column.key} style={getHeaderCellStyle(column)}>
									{column.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={columns.length} style={{ ...tdStyle, textAlign: "center", padding: 24, color: "#6b7280" }}>
									กำลังโหลดข้อมูล...
								</td>
							</tr>
						) : memberList.length === 0 ? (
							<tr>
								<td colSpan={columns.length} style={{ ...tdStyle, textAlign: "center", padding: 24, color: "#6b7280" }}>
									ไม่พบข้อมูลผู้เล่น
								</td>
							</tr>
						) : (
							memberList.map((item, index) => {
								const globalIndex = (page - 1) * limit + index + 1
								return (
									<tr key={item?._id || item?.id || item?.userId || index}>
										{columns.map((column) => {
											const content = column.render ? column.render(item, { globalIndex, rowIndex: index }) : item[column.key]
											return (
												<td key={column.key} style={getBodyCellStyle(column)}>
													{content}
												</td>
											)
										})}
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>

			<PaginationNew page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={handleChangePage} onLimitChange={handleChangeLimit} />
		</div>
	)
}

export default MemberVoteList
